import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CheckCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { CreateExamRequest, CreateExamSubjectRequest, ExamRow, ExamSubjectRow, ExamType } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Exam setup + datesheet.
 * When `:code` param is present, shows one exam's detail (subjects + progress
 * + publish); otherwise the full exam list.
 */
export function ExamSetupPage() {
  const { institution = '', code } = useParams<{ institution: string; code?: string }>();
  if (code) return <ExamDetailInner institution={institution} examId={code} />;
  return <ExamListInner institution={institution} />;
}

function ExamListInner({ institution }: { institution: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const list = useQuery({
    queryKey: ['exams', institution],
    queryFn: () => productApi.listExams(institution),
    enabled: !!institution,
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Exams</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            One row per assessment cycle. Add subjects → enter marks → publish.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>Create exam</Button>
      </div>

      <Card size="small" title={`${list.data?.total ?? 0} exams`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {list.data && list.data.rows.length === 0 && (
          <Empty description={<span>No exams yet. <a onClick={() => setOpen(true)}>Create the first one</a>.</span>} style={{ padding: 20 }} />
        )}
        {list.data && list.data.rows.length > 0 && (
          <Table<ExamRow>
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={list.data.rows}
            onRow={(r) => ({ onClick: () => navigate(`/i/${institution}/admin/exams/${r.id}`), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'n', render: (v: string) => <b>{v}</b> },
              { title: 'Type', dataIndex: 'exam_type', key: 't', render: (v: ExamType) => <Tag>{v.replace(/_/g, ' ')}</Tag> },
              { title: 'Subjects', dataIndex: 'subject_count', key: 's', width: 100, align: 'right' },
              {
                title: 'Status', dataIndex: 'status', key: 'st', width: 110,
                render: (v: string) => <Tag color={v === 'published' ? 'success' : v === 'closed' ? 'default' : 'processing'}>{v}</Tag>,
              },
            ]}
          />
        )}
      </Card>

      <CreateExamModal
        open={open}
        institution={institution}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          void qc.invalidateQueries({ queryKey: ['exams', institution] });
          void message.success('Exam created');
        }}
      />
    </Space>
  );
}

function ExamDetailInner({ institution, examId }: { institution: string; examId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [subjectOpen, setSubjectOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['exam', institution, examId],
    queryFn: () => productApi.getExam(institution, examId),
    enabled: !!institution && !!examId,
  });
  const subjects = useQuery({
    queryKey: ['subjects', institution],
    queryFn: () => productApi.listSubjects(institution),
    enabled: !!institution,
  });

  const publish = useMutation({
    mutationFn: () => productApi.publishExam(institution, examId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exam', institution, examId] });
      void qc.invalidateQueries({ queryKey: ['exams', institution] });
      void message.success('Published');
    },
    onError: (e) => void message.error(errMsg(e) ?? 'Publish failed'),
  });

  if (detail.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!detail.data) return <Alert type="error" showIcon message="Exam not found" />;
  const { exam, subjects: rows, progress_entered, progress_expected } = detail.data;
  const percent = progress_expected > 0 ? Math.round((progress_entered / progress_expected) * 100) : 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space size={4}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/exams`)}>
          ← Exams
        </Button>
        <Typography.Text type="secondary">/</Typography.Text>
        <Typography.Text style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{exam.name}</Typography.Text>
      </Space>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>{exam.name}</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {exam.exam_type.replace(/_/g, ' ')} · {rows.length} subjects · marks entered {progress_entered} / {progress_expected} ({percent}%)
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setSubjectOpen(true)}>Add subject</Button>
          {exam.status === 'draft' ? (
            <Popconfirm title="Publish exam?" description="Datesheet becomes visible to teachers." onConfirm={() => publish.mutate()} okText="Publish">
              <Button type="primary" icon={<CheckCircleOutlined />} loading={publish.isPending}>Publish</Button>
            </Popconfirm>
          ) : (
            <Tag color="success">{exam.status}</Tag>
          )}
        </Space>
      </div>

      <Card size="small" title={`Datesheet (${rows.length})`}>
        {rows.length === 0 ? (
          <Empty description={<span>No subjects yet. <a onClick={() => setSubjectOpen(true)}>Add the first subject</a>.</span>} style={{ padding: 20 }} />
        ) : (
          <Table<ExamSubjectRow>
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Subject', key: 'sub', render: (_, r) => <b>{r.subject_name}</b> },
              { title: 'Code', dataIndex: 'subject_code', key: 'c', width: 80 },
              { title: 'Section', key: 'sec', width: 100, render: (_, r) => `${r.grade}${r.section ? `-${r.section}` : ''}` },
              { title: 'Date', dataIndex: 'date', key: 'd', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Max', dataIndex: 'max_marks', key: 'm', width: 60, align: 'right' },
              { title: 'Passing', dataIndex: 'passing_marks', key: 'p', width: 80, align: 'right' },
              {
                title: 'Progress',
                key: 'prog',
                width: 140,
                render: (_, r) => {
                  const pct = r.marks_expected > 0 ? Math.round((r.marks_entered / r.marks_expected) * 100) : 0;
                  return <Tag color={pct === 100 ? 'success' : pct > 0 ? 'processing' : 'default'}>{r.marks_entered} / {r.marks_expected} · {pct}%</Tag>;
                },
              },
              {
                key: 'action',
                width: 140,
                align: 'right',
                render: (_, r) => (
                  <Button size="small" type="primary" onClick={() => navigate(`/i/${institution}/admin/exams/${exam.id}/marks?subject=${r.id}`)}>
                    Enter marks
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      <AddSubjectModal
        open={subjectOpen}
        institution={institution}
        examId={exam.id}
        subjects={subjects.data?.rows ?? []}
        onClose={() => setSubjectOpen(false)}
        onCreated={() => {
          setSubjectOpen(false);
          void qc.invalidateQueries({ queryKey: ['exam', institution, examId] });
          void message.success('Subject added');
        }}
      />
    </Space>
  );
}

function CreateExamModal({
  open, institution, onClose, onCreated,
}: { open: boolean; institution: string; onClose: () => void; onCreated: () => void }) {
  const [form] = Form.useForm<{ session_id: string; name: string; exam_type: ExamType }>();
  const cal = useQuery({
    queryKey: ['calendar', institution],
    queryFn: () => productApi.getCalendar(institution),
    enabled: !!institution && open,
  });
  const current = cal.data?.current_session ?? null;
  const m = useMutation({
    mutationFn: (v: CreateExamRequest) => productApi.createExam(institution, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Create exam" onCancel={() => { m.reset(); onClose(); }} okText="Create" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      {(!cal.data || cal.data.sessions.length === 0) ? (
        <Alert type="warning" showIcon message="No academic session yet. Add one in Setup step 2." />
      ) : (
        <Form form={form} layout="vertical" initialValues={{ session_id: current?.id, exam_type: 'unit' }} onFinish={(v) => m.mutate({ session_id: v.session_id, name: v.name.trim(), exam_type: v.exam_type })}>
          <Form.Item name="session_id" label="Session" rules={[{ required: true }]}>
            <Select options={(cal.data?.sessions ?? []).map((s) => ({ value: s.id, label: `${s.label}${s.is_current ? ' · current' : ''}` }))} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Unit test 1 · Term 2" autoFocus />
          </Form.Item>
          <Form.Item name="exam_type" label="Type" rules={[{ required: true }]}>
            <Select options={[
              { value: 'unit', label: 'Unit test' },
              { value: 'half_yearly', label: 'Half-yearly' },
              { value: 'annual', label: 'Annual' },
              { value: 'other', label: 'Other' },
            ]} />
          </Form.Item>
          {err && <Alert type="error" showIcon message={err} />}
        </Form>
      )}
    </Modal>
  );
}

function AddSubjectModal({
  open, institution, examId, subjects, onClose, onCreated,
}: {
  open: boolean; institution: string; examId: string;
  subjects: Array<{ id: string; name: string; code: string }>;
  onClose: () => void; onCreated: () => void;
}) {
  const [form] = Form.useForm<{ subject_id: string; grade: string; section: string; date: string; max_marks: number; passing_marks: number }>();
  const students = useQuery({
    queryKey: ['students', institution, {}],
    queryFn: () => productApi.listStudents(institution),
    enabled: !!institution && open,
  });
  const sections = useMemo(() => {
    const set = new Map<string, { grade: string; section: string }>();
    for (const s of students.data?.rows ?? []) {
      set.set(`${s.grade}::${s.section}`, { grade: s.grade, section: s.section });
    }
    return Array.from(set.values());
  }, [students.data]);

  const m = useMutation({
    mutationFn: (v: CreateExamSubjectRequest) => productApi.createExamSubject(institution, examId, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add subject to datesheet" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      {subjects.length === 0 ? (
        <Alert type="warning" showIcon message="No subjects yet. Add one on the Timetable page first." />
      ) : sections.length === 0 ? (
        <Alert type="warning" showIcon message="No students / sections yet." />
      ) : (
        <Form form={form} layout="vertical" initialValues={{ max_marks: 100, passing_marks: 33 }}
          onFinish={(v) => {
            const [grade, section] = v.grade.split('::');
            m.mutate({
              exam_id: examId,
              subject_id: v.subject_id,
              grade: grade ?? '',
              section: section ?? '',
              date: v.date,
              max_marks: v.max_marks,
              passing_marks: v.passing_marks,
            });
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="subject_id" label="Subject" rules={[{ required: true }]}>
              <Select options={subjects.map((s) => ({ value: s.id, label: `${s.name} (${s.code})` }))} />
            </Form.Item>
            <Form.Item name="grade" label="Section" rules={[{ required: true }]}>
              <Select options={sections.map((s) => ({ value: `${s.grade}::${s.section}`, label: `${s.grade}${s.section ? `-${s.section}` : ''}` }))} />
            </Form.Item>
            <Form.Item name="date" label="Date" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}>
              <Input placeholder="yyyy-mm-dd" />
            </Form.Item>
            <Form.Item name="max_marks" label="Max marks" rules={[{ required: true }]}>
              <InputNumber min={1} max={1000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="passing_marks" label="Passing marks" rules={[{ required: true }]}>
              <InputNumber min={0} max={1000} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          {err && <Alert type="error" showIcon message={err} />}
        </Form>
      )}
    </Modal>
  );
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
